import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/common/Spinner';
import ProtectedRouteComponent from '../components/common/ProtectedRoute';

// Layouts
const MainLayout = lazy(() => import('../components/layout/MainLayout'));
const AuthLayout = lazy(() => import('../components/layout/AuthLayout'));

// Public pages
const HomePage = lazy(() => import('../pages/home/HomePage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const SubscriptionPage = lazy(() => import('../pages/auth/SubscriptionPage'));
const FaqPage = lazy(() => import('../pages/home/FaqPage'));

const BitcoinPage = lazy(() => import('../pages/bitcoin/BitcoinPage'));
const ConsultoriaPage = lazy(() => import('../pages/consultoria/ConsultoriaPage'));
const PaymentSuccess = lazy(() => import('../pages/PaymentSuccess'));
const PaymentCancelled = lazy(() => import('../pages/PaymentCancelled'));

// Protected pages
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const PortfolioPage = lazy(() => import('../pages/portfolio/PortfolioPage'));
const TransactionsPage = lazy(() => import('../pages/transactions/TransactionsPage'));
const WeeklyTradePage = lazy(() => import('../pages/trades/WeeklyTradePage'));
const ManualTransactionPage = lazy(() => import('../pages/transactions/ManualTransactionPage'));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage'));
const ReportsPage = lazy(() => import('../pages/reports/ReportsPage'));
const RSIHistoricalPage = lazy(() => import('../pages/RSIHistoricalPage'));
const GuidePage = lazy(() => import('../pages/guide/GuidePage'));
const SubscriptionExpiredPage = lazy(() => import('../pages/subscription/SubscriptionExpiredPage'));

// Admin pages
const AdminUserCreationPage = lazy(() => import('../pages/admin/AdminUserCreationPage'));
const AdminUsersListPage = lazy(() => import('../pages/admin/AdminUsersListPage'));
const AdminSystemDashboard = lazy(() => import('../pages/admin/AdminSystemDashboard'));
const AdminPortfoliosPage = lazy(() => import('../pages/admin/AdminPortfoliosPage'));
const AdminLogsPage = lazy(() => import('../pages/admin/AdminLogsPage'));


/**
 * Protected route component that requires authentication
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" withText text="Checking authentication..." />
      </div>
    );
  }

  // Redireciona se não autenticado OU se o objeto user não tem username válido
  if (!isAuthenticated || !user?.username) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Public route component, redirects to dashboard if already authenticated
 */
const PublicRoute = ({ children, redirectIfAuthenticated = true }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" withText text="Checking authentication..." />
      </div>
    );
  }
  
  if (isAuthenticated && redirectIfAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

/**
 * Main routing component
 * ✅ PHASE 4: All user-visible text in English
 */
const AppRoutes = () => {
  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <Spinner size="lg" withText text="Loading..." />
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <HomePage />
              </PublicRoute>
            }
          />
          
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          
          <Route
            path="/register"
            element={
              <PublicRoute>
                <AuthLayout>
                  <RegisterPage />
                </AuthLayout>
              </PublicRoute>
            }
          />
          
          <Route
            path="/subscription"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <SubscriptionPage />
              </PublicRoute>
            }
          />
          
          <Route
            path="/faq"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <FaqPage />
              </PublicRoute>
            }
          />



          <Route
            path="/bitcoin-do-zero"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <BitcoinPage />
              </PublicRoute>
            }
          />

          <Route
            path="/consultoria-bitcoin"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <ConsultoriaPage />
              </PublicRoute>
            }
          />

          {/* Redirect /bitcoin to /bitcoin-do-zero for backward compatibility */}
          <Route
            path="/bitcoin"
            element={<Navigate to="/bitcoin-do-zero" replace />}
          />

          {/* Payment routes */}
          <Route
            path="/payment-success"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <PaymentSuccess />
              </PublicRoute>
            }
          />
          
          <Route
            path="/payment-cancelled"
            element={
              <PublicRoute redirectIfAuthenticated={false}>
                <PaymentCancelled />
              </PublicRoute>
            }
          />
          
          {/* Protected routes */}
          {/* Assinatura vencida: fora do MainLayout (as chamadas do layout também seriam bloqueadas) */}
          <Route
            path="/renew"
            element={
              <ProtectedRoute>
                <SubscriptionExpiredPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PortfolioPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TransactionsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/transactions/new"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ManualTransactionPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/weekly-trade"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <WeeklyTradePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          {/* Redirect old dynamic-cash route to portfolio page */}
          <Route
            path="/dynamic-cash"
            element={<Navigate to="/portfolio" replace />}
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReportsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/rsi-historical"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <RSIHistoricalPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/guide"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <GuidePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin routes - Protected and require admin privileges */}
          <Route
            path="/admin"
            element={<Navigate to="/admin/system" replace />}
          />
          
          <Route
            path="/admin/system"
            element={
              <ProtectedRouteComponent requireAdmin={true}>
                <MainLayout>
                  <AdminSystemDashboard />
                </MainLayout>
              </ProtectedRouteComponent>
            }
          />
          
          <Route
            path="/admin/users"
            element={
              <ProtectedRouteComponent requireAdmin={true}>
                <MainLayout>
                  <AdminUsersListPage />
                </MainLayout>
              </ProtectedRouteComponent>
            }
          />
          
          <Route
            path="/admin/users/create"
            element={
              <ProtectedRouteComponent requireAdmin={true}>
                <MainLayout>
                  <AdminUserCreationPage />
                </MainLayout>
              </ProtectedRouteComponent>
            }
          />
          
          <Route
            path="/admin/portfolios"
            element={
              <ProtectedRouteComponent requireAdmin={true}>
                <MainLayout>
                  <AdminPortfoliosPage />
                </MainLayout>
              </ProtectedRouteComponent>
            }
          />
          
          <Route
            path="/admin/logs"
            element={
              <ProtectedRouteComponent requireAdmin={true}>
                <MainLayout>
                  <AdminLogsPage />
                </MainLayout>
              </ProtectedRouteComponent>
            }
          />
          

          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRoutes; 